import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { activeClientService } from '@/services/active-client';
import { authStorage } from '@/lib/auth-storage';
import type { ActiveClientSummary } from '@just-frotas/shared';
import { useAuth } from './AuthContext';

// CORE multi-tenancy: cada sessao opera em nome de UM cliente ativo.
// - Cliente regular: sempre ele mesmo (sem prompt).
// - Cliente master / SuperAdmin: pode trocar entre clientes acessiveis.
//
// Pra Fase 1 expomos apenas a lista e troca; modal de selecao + KPIs por
// cliente entram nas Fases 2-5 conforme paginas precisarem.

interface ActiveClientContextValue {
  clientes: ActiveClientSummary[];
  // activeClientUuid e o UUID do cliente atualmente ativo (era INT pre-UUID).
  activeClientUuid: string | null;
  active: ActiveClientSummary | null;
  loading: boolean;
  reload: () => Promise<void>;
  switchClient: (clienteUuid: string) => Promise<void>;
}

const ActiveClientContext = createContext<ActiveClientContextValue | undefined>(undefined);

interface ProviderProps {
  children: ReactNode;
}

export function ActiveClientProvider({ children }: ProviderProps) {
  const { user, refreshMe } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [clientes, setClientes] = useState<ActiveClientSummary[]>([]);
  const [activeClientUuid, setActiveClientUuid] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const reload = useCallback(async () => {
    if (!user) {
      setClientes([]);
      setActiveClientUuid(null);
      return;
    }
    setLoading(true);
    try {
      const resp = await activeClientService.list();
      setClientes(resp.clientes);
      setActiveClientUuid(resp.active);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    reload().catch(() => {
      /* silencioso: se falhar (sem permissao, etc), lista fica vazia */
    });
  }, [reload]);

  const switchClient = useCallback(
    async (clienteUuid: string) => {
      const resp = await activeClientService.switch(clienteUuid);
      authStorage.setToken(resp.accessToken);
      setActiveClientUuid(resp.activeClient.id);
      // user.activeClientUuid mudou nas claims - refetch /auth/me pra refletir.
      await refreshMe();
      // Reset total de cache: tudo que estava carregado pertencia ao cliente
      // anterior (cartoes, veiculos, motoristas, etc). Sem clear, telas
      // continuam mostrando dados stale e novos clicks podem disparar 404
      // (tenant scope do backend rejeita recurso de outro cliente).
      queryClient.clear();
      // Volta pra dashboard - simula reinicio de sessao (UX pretendida).
      navigate('/', { replace: true });
    },
    [refreshMe, queryClient, navigate],
  );

  const active = useMemo(
    () => clientes.find((c) => c.id === activeClientUuid) ?? null,
    [clientes, activeClientUuid],
  );

  const value = useMemo<ActiveClientContextValue>(
    () => ({ clientes, activeClientUuid, active, loading, reload, switchClient }),
    [clientes, activeClientUuid, active, loading, reload, switchClient],
  );

  return <ActiveClientContext.Provider value={value}>{children}</ActiveClientContext.Provider>;
}

export function useActiveClient(): ActiveClientContextValue {
  const ctx = useContext(ActiveClientContext);
  if (!ctx) throw new Error('useActiveClient deve ser usado dentro de <ActiveClientProvider>');
  return ctx;
}
